import { UserRole } from '@interfaces/auth';
import { useEffect, useState } from 'react';
import useToastContext from '@contexts/ToastContext/hook';
import { parseFilters } from '@utils/parseFilters';
import { findBotFunnel } from '@api/reports';
import { BotFunnelResponse } from '@interfaces/reports/botFunnel';
import { resolveFunnelCoverage } from '@utils/resolveFunnelCoverage';

export type BotFunnelFiltersInput = {
  botConfigIds: number[];
  startDate: Date;
  endDate: Date;
  clientTz: string;
  /** Carried only so the drill-down link can hand the period label over, not used in the request. */
  periodRange?: string;
  /**
   * Same idea: the funnel request has no role - an affiliate never sees this block at all - but the
   * screen the "Ver detalhes" link points at does, and its sibling link from PaymentConversionSummary
   * already carries it. Without this the two links out of the same dashboard behave differently, and
   * the funnel one only lands on the right filter because the destination remembers one in
   * localStorage.
   *
   * `UserRole` e não `string`: o campo é repassado a quem monta o `userRoles` da requisição, e
   * `string` fazia aquele ponto aceitar qualquer coisa — inclusive um papel que a API recusa.
   */
  userRole?: UserRole;
};

export const emptyResponse: BotFunnelResponse = {
  collectionStartedAt: null,
  stages: [],
  attemptsOpened: { attempts: 0, leads: 0 },
  purchaseRate: 0,
  purchasers: 0,
  leadsPerPurchaser: 0,
  starts: { total: 0, unique: 0 },
  paidSales: 0,
  startToSaleRate: 0,
  startsPerSale: 0,
  byOrigin: [],
  byOffer: [],
  byPaymentMethod: [],
  series: [],
};

/**
 * Where leads drop off between opening the bot and paying.
 *
 * Scope is the BOT, never the platform: a sale made on the web checkout only lands in this funnel when
 * the lead showed checkout intent in the bot first. A producer who sells mostly through the web will
 * see a low rate here and that is correct - the tooltip has to say so, or it becomes a support ticket.
 */

/**
 * Fills in whatever the response left out.
 *
 * The response was previously stored as-is, so a field the API had not started returning yet was simply
 * absent - and reading .length off it threw, blanking the WHOLE conversion screen rather than hiding one
 * table. That is the same failure the coverage gate exists to prevent, in a different disguise: this
 * screen must degrade by showing less, never by showing nothing.
 *
 * Merged field by field rather than spread over the defaults, because a field present and explicitly null
 * would defeat a spread.
 */
type IncomingResponse = Partial<
  Omit<
    BotFunnelResponse,
    'stages' | 'attemptsOpened' | 'byOrigin' | 'series'
  > & {
    stages: Partial<BotFunnelResponse['stages'][number]>[];
    attemptsOpened: { attempts?: number; leads?: number };
    byOrigin: Partial<BotFunnelResponse['byOrigin'][number]>[];
    series: Partial<BotFunnelResponse['series'][number]>[];
  }
>;

const normalise = (data?: IncomingResponse): BotFunnelResponse => ({
  collectionStartedAt: data?.collectionStartedAt ?? null,
  stages: (data?.stages ?? []).map((row) => ({
    stage: row.stage ?? '',
    leads: row.leads ?? 0,
    attempts: row.attempts ?? 0,
    events: row.events ?? 0,
    rateFromStart: row.rateFromStart ?? 0,
    rateFromTop: row.rateFromTop ?? 0,
    rateFromPrevious: row.rateFromPrevious ?? 0,
  })),
  attemptsOpened: {
    attempts: data?.attemptsOpened?.attempts ?? 0,
    leads: data?.attemptsOpened?.leads ?? 0,
  },
  purchaseRate: data?.purchaseRate ?? 0,
  purchasers: data?.purchasers ?? 0,
  leadsPerPurchaser: data?.leadsPerPurchaser ?? 0,
  starts: data?.starts ?? { total: 0, unique: 0 },
  paidSales: data?.paidSales ?? 0,
  startToSaleRate: data?.startToSaleRate ?? 0,
  startsPerSale: data?.startsPerSale ?? 0,
  // Rows are rebuilt field by field rather than passed through, for the reason the whole function exists:
  // report-api#61 widened these two to carry the top of the funnel, and an API predating it returns rows
  // with only the /start columns. Spreading would leave `leads` undefined inside a typed row, and the
  // table would render "undefined" rather than degrade.
  byOrigin: (data?.byOrigin ?? []).map((row) => ({
    origin: row.origin ?? '',
    attemptsOpened: row.attemptsOpened ?? 0,
    leads: row.leads ?? 0,
    starts: row.starts ?? 0,
    paidSales: row.paidSales ?? 0,
    purchasers: row.purchasers ?? 0,
    purchaseRate: row.purchaseRate ?? 0,
    startToSaleRate: row.startToSaleRate ?? 0,
  })),
  byOffer: data?.byOffer ?? [],
  byPaymentMethod: data?.byPaymentMethod ?? [],
  series: (data?.series ?? []).map((row) => ({
    period: row.period ?? '',
    attemptsOpened: row.attemptsOpened ?? 0,
    leads: row.leads ?? 0,
    starts: row.starts ?? 0,
    paidSales: row.paidSales ?? 0,
    purchasers: row.purchasers ?? 0,
    purchaseRate: row.purchaseRate ?? 0,
    startToSaleRate: row.startToSaleRate ?? 0,
  })),
});

/**
 * Loads the funnel and works out how much of the selected period it actually measured.
 *
 * Shared by the dashboard summary and the detail screen so the coverage decision is made in ONE place.
 * That gate is not cosmetic: if the API omits collectionStartedAt, coverage resolves to unmeasured while
 * the counts arrive as usual, and a screen gating on counts alone would render a partially collected
 * funnel with no warning - the exact thing this feature exists to prevent.
 */
export function useBotFunnel(filters: BotFunnelFiltersInput) {
  const toast = useToastContext();
  const [data, setData] = useState<BotFunnelResponse>(emptyResponse);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * A request that FAILED, as opposed to a funnel nobody entered.
   *
   * The catch falls back to the empty response, and hasData then reads false - which this card renders as
   * "nobody started a purchase in this period". That is a real and common answer, so a broken request
   * hides inside it perfectly.
   */
  const [hasFailed, setHasFailed] = useState(false);
  /** A chave dos filtros a que `data` responde. Ver a nota de `isStale`, no fim. */
  const [respondidoPara, setRespondidoPara] = useState<string | null>(null);
  const chaveAtual = JSON.stringify(filters ?? null);

  useEffect(() => {
    // Guards against a superseded request: changing period or communities leaves the previous one in
    // flight, and without this whichever finishes last commits - including its finally clearing the
    // loading state of a request that has not finished.
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(emptyResponse);
      setHasFailed(false);
      // Sem comunidade não há busca, e a resposta vazia é a resposta DESTE recorte.
      setRespondidoPara(chaveAtual);
      // Clears the spinner explicitly. Deselecting every community while a request is in flight
      // invalidates that request, so its finally no longer does this - and without it the card would
      // spin forever instead of showing the empty state.
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    setIsLoading(true);
    // Fields are listed rather than spread: periodRange is a display label carried for the drill-down link,
    // and parseFilters keeps it, so spreading would post it to an endpoint whose DTO does not declare it -
    // coupling the backend contract to a UI string. The payment request already did this.
    const parsed = parseFilters(filters);

    findBotFunnel({
      botConfigIds: parsed.botConfigIds,
      startDate: parsed.startDate,
      endDate: parsed.endDate,
      clientTz: parsed.clientTz,
      timeAggregationPeriod: 'DAY',
    })
      .then((response) => {
        if (!active) return;
        setHasFailed(false);
        // normalise takes an optional partial, so it covers a missing body as well as missing fields.
        setData(normalise(response.data));
      })
      .catch((error) => {
        if (!active) return;
        toast.handleError(error, 'Não foi possível carregar os registros');
        setData(emptyResponse);
        setHasFailed(true);
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
        /*
          A chave entra junto do commit do dado, e como ESTADO.

          Como `ref` isto tinha dois defeitos: marcar a chave num efeito separado podia carimbá-la antes
          de o dado chegar, e — pior — trocar de filtro com a busca anterior em voo deixava a chave
          velha, porque o efeito só marcava quando `isLoading` era falso. Aí a resposta nova chegava, o
          render calculava `isStale` contra a chave velha, e escrever num `ref` não re-renderiza: o
          card ficava suprimido até alguma outra coisa provocar um render.
        */
        setRespondidoPara(chaveAtual);
      });

    return () => {
      active = false;
    };
  }, [filters]);

  const coverage = resolveFunnelCoverage(
    data.collectionStartedAt,
    filters.startDate,
    filters.endDate,
  );
  /**
   * Data is shown only when there is something to show AND we know what it covers.
   *
   * Coverage is part of the gate, not decoration. If this ships before the API returns
   * collectionStartedAt, coverage resolves to unmeasured while the counts arrive as usual, and gating on
   * hasData alone would render a partially collected funnel with no warning at all - the exact thing this
   * card exists to prevent. Failing to the empty panel is the safe direction.
   */
  const isMeasured = coverage.kind !== 'unmeasured';
  // Either signal counts, and that is deliberate.
  //
  // Gating on /starts alone was wrong: a community whose leads all arrive on deep links or group join
  // requests can be fully instrumented and still report zero starts, which drew the empty panel over a
  // funnel with real numbers in every row below the top.
  //
  // Gating on attempts alone would be wrong in the other direction, and only until one deploy caught up:
  // an API that has not shipped attemptsOpened yet normalises to zero attempts, and the whole funnel
  // would vanish rather than degrade. Reading both means neither deploy order blanks the screen.
  const hasCounts = data.attemptsOpened.attempts > 0 || data.starts.total > 0;
  const hasData = hasCounts && isMeasured;
  /**
   * Os filtros a que `data` responde, comparados com os filtros de agora.
   *
   * `isLoading` não basta para quem precisa saber se o dado é do recorte atual: ele só vira `true`
   * dentro do efeito, que roda DEPOIS do commit. No render em que o período muda existe uma passada com
   * `isLoading === false` e o dado do período anterior — e quem decidir alguma coisa nessa passada
   * decide pelo dado velho achando que é o novo.
   *
   * Mesmo padrão do `statusIds` de `useDiscoveryCard`: guardar a que recorte o dado pertence, em vez de
   * confiar num sinalizador que chega um render atrasado.
   */
  const isStale = respondidoPara !== chaveAtual;

  return { data, coverage, hasData, isLoading, isStale, hasFailed };
}
